/**
 * 계약 관리 달력 보기(C3b) — 월 달력의 날짜 칸에 «그날이 기준일인 계약»을 하루짜리 막대로 그린다(막대를 누르면 계약 상세).
 * 칸 계산·줄 배치는 경영지원팀 보드의 순수 함수를 빌려 쓰고, 그리기는 여기서 한다(업무 캘린더 TaskPmCalendar 와 같은 방식).
 *
 * - 🔴 CalendarGrid.jsx 는 건드리지 않는다 — 순수 함수 `buildMonthCells`·`layoutSchedulesForMonth` 둘만 빌려 쓴다.
 * - 보이는 42칸 범위만 묻는다(목록 API due_from·due_to) · 부서·검색어는 목록 화면의 것을 그대로 싣는다.
 * - 분류는 체크 상자로 화면에서만 거른다(다시 묻지 않는다).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { buildMonthCells, layoutSchedulesForMonth } from '../supportboard/CalendarGrid';
import { contractsAPI } from '../../api/client';
import { COLORS, RADIUS, btn, card } from '../../utils/uiStyles';
import { kstToday } from '../../utils/kstTime';
import { CATEGORY_LABELS, errorText, isUrgent } from './contractLabels';

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
// 막대 치수는 경영지원팀 보드 달력과 같다(머리 숫자 아래 30 에서 시작 · 줄 높이 23 · 막대 19).
const BAR_TOP = 30;
const LANE_HEIGHT = 23;
// 목록 API 상한(limit ≤ 1000)까지 받는다 — 넘으면 안내만 한다.
const CALENDAR_LIMIT = 1000;
const CATEGORY_CODES = Object.keys(CATEGORY_LABELS);

// 'YYYY-MM-DD' → { year, month(0-11) }
const monthOf = (key) => ({ year: Number(String(key).slice(0, 4)), month: Number(String(key).slice(5, 7)) - 1 });

export default function ContractsCalendar({ department, q, reloadKey, onOpen, todayKey = kstToday() }) {
  const [ym, setYm] = useState(() => monthOf(todayKey));
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [checked, setChecked] = useState(() => new Set(CATEGORY_CODES));
  // 늦게 온 응답이 최신 달을 덮지 않게 요청마다 번호를 매긴다(화면을 떠나면 번호를 올려 다 버린다).
  const seqRef = useRef(0);

  useEffect(() => () => { seqRef.current += 1; }, []);

  const cells = useMemo(() => buildMonthCells(ym.year, ym.month), [ym]);
  const from = cells[0].date;
  const to = cells[cells.length - 1].date;

  // ── 보이는 42칸 범위만 묻는다(분류는 화면에서 거르므로 싣지 않는다).
  useEffect(() => {
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    const params = { limit: CALENDAR_LIMIT, offset: 0, due_from: from, due_to: to };
    if (department) params.department = department;
    if (q) params.q = q;
    // 🔴 보낼 때 오류를 비운다 — 한 번 실패한 문구가 다음 성공 뒤에 남지 않게.
    setError('');
    contractsAPI.list(params)
      .then((r) => {
        if (seqRef.current !== seq) return;          // 지나간 요청의 응답은 버린다
        setItems(Array.isArray(r?.data?.items) ? r.data.items : []);
        setTotal(Number(r?.data?.total) || 0);
      })
      .catch(async (e) => {
        const message = await errorText(e, '달력 계약을 불러오지 못했습니다.');
        if (seqRef.current !== seq) return;          // 🔴 errorText 는 async 다 — 기다린 뒤에 번호를 다시 본다
        // 🔴 실패하면 막대·안내를 비운다 — 옛 total 로 «1,000건만» 이 같이 뜨지 않게.
        setItems([]);
        setTotal(0);
        setError(message);
      });
  }, [department, q, from, to, reloadKey]);

  // 계약 하나 = 기준일 하루짜리 막대. 기준일이 없는 계약은 달력에 못 놓으므로 뺀다.
  // 서버 분류는 A~I 뿐이지만, 모르는 값이 오면 거르지 않고 보인다(체크 상자로 끌 수 없는 막대가 사라지지 않게).
  const schedules = useMemo(() => items
    .filter((it) => it && it.due_date && (!CATEGORY_LABELS[it.category] || checked.has(it.category)))
    .map((it) => {
      const base = [it.contract_no, it.contract_name].filter(Boolean).join(' ');
      return {
        id: it.id,
        start_date: it.due_date,
        end_date: it.due_date,
        label: it.due_kind === 'notice' ? `통보 ${base}` : base,
        urgent: isUrgent(it.days_left),
      };
    }), [items, checked]);

  const laidOutCells = useMemo(() => layoutSchedulesForMonth(schedules, cells), [schedules, cells]);
  const laneCount = laidOutCells.reduce(
    (max, cell) => cell.items.reduce((cellMax, item) => Math.max(cellMax, item.lane + 1), max),
    0,
  );
  const cellHeight = Math.max(94, BAR_TOP + laneCount * LANE_HEIGHT + 8);

  const toggleCategory = (code) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    return next;
  });

  const shiftMonth = (delta) => setYm((prev) => {
    const d = new Date(prev.year, prev.month + delta, 1, 12);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  return (
    <section
      aria-label={`${ym.year}년 ${ym.month + 1}월 계약 달력`}
      style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.lg, padding: '16px', overflowX: 'auto' }}
    >
      {/* 머리 줄 — 가운데에 ‹ 달 › 묶음, 오른쪽 끝에 «오늘»(업무 캘린더와 같은 꼴). */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', marginBottom: '10px' }}>
        <div />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button type="button" aria-label="이전 달" onClick={() => shiftMonth(-1)} style={btn('secondary', 'sm')}>‹</button>
          <h3 style={{ margin: 0, color: COLORS.text, fontSize: '17px' }}>{ym.year}년 {ym.month + 1}월</h3>
          <button type="button" aria-label="다음 달" onClick={() => shiftMonth(1)} style={btn('secondary', 'sm')}>›</button>
        </div>
        <button type="button" onClick={() => setYm(monthOf(todayKey))}
          style={{ ...btn('secondary', 'sm'), justifySelf: 'end' }}>오늘</button>
      </div>

      <div role="group" aria-label="분류 거르기" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginBottom: '12px', fontSize: '12px' }}>
        {CATEGORY_CODES.map((code) => (
          <label key={code} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={checked.has(code)} onChange={() => toggleCategory(code)} />
            {code} {CATEGORY_LABELS[code]}
          </label>
        ))}
      </div>

      <div style={{ minWidth: '700px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {WEEKDAY_LABELS.map((label, index) => (
            <div
              key={label}
              style={{
                padding: '7px 4px',
                textAlign: 'center',
                color: index === 0 ? COLORS.dangerText : index === 6 ? COLORS.info : COLORS.textSub,
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {label}
            </div>
          ))}
        </div>

        <div
          role="grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            borderTop: `1px solid ${COLORS.border}`,
            borderLeft: `1px solid ${COLORS.border}`,
          }}
        >
          {laidOutCells.map((cell, cellIndex) => {
            const isToday = cell.date === todayKey;
            return (
              <div
                role="gridcell"
                key={cell.date}
                aria-label={cell.date}
                style={{
                  position: 'relative',
                  minWidth: 0,
                  height: `${cellHeight}px`,
                  boxSizing: 'border-box',
                  background: cell.inCurrentMonth ? COLORS.surface : COLORS.bg,
                  borderRight: `1px solid ${COLORS.border}`,
                  borderBottom: `1px solid ${COLORS.border}`,
                  overflow: 'hidden',
                }}
              >
                {/* 오늘은 날짜 숫자를 원으로 감싼다. */}
                <span
                  style={{
                    position: 'absolute',
                    top: isToday ? '4px' : '6px',
                    right: isToday ? '6px' : '8px',
                    width: isToday ? '22px' : undefined,
                    height: isToday ? '22px' : undefined,
                    borderRadius: isToday ? '50%' : undefined,
                    background: isToday ? COLORS.primary : undefined,
                    display: isToday ? 'inline-flex' : undefined,
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isToday
                      ? '#fff'
                      : cell.inCurrentMonth
                        ? (cellIndex % 7 === 0 ? COLORS.dangerText : cellIndex % 7 === 6 ? COLORS.info : COLORS.text)
                        : COLORS.textMuted,
                    fontSize: '12px',
                    fontWeight: isToday || cell.inCurrentMonth ? 700 : 500,
                  }}
                >
                  {cell.day}
                </span>

                {/* 하루짜리 막대라 칸마다 제목 조각이다 — 전부 단추로 그린다. */}
                {cell.items.map((item) => {
                  const schedule = item.schedule;
                  return (
                    <button
                      key={`${schedule.id}-${cell.date}`}
                      type="button"
                      aria-label={schedule.label}
                      title={schedule.label}
                      onClick={() => { if (onOpen) onOpen(schedule.id); }}
                      style={{
                        position: 'absolute',
                        top: `${BAR_TOP + item.lane * LANE_HEIGHT}px`,
                        left: '4px',
                        right: '4px',
                        height: '19px',
                        boxSizing: 'border-box',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        display: 'block',
                        border: 'none',
                        textAlign: 'left',
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                        fontSize: '11px',
                        lineHeight: '15px',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        // 30일 안(지남 포함)은 위험색 — 목록의 «남은 날» 강조와 같은 기준(isUrgent).
                        background: schedule.urgent ? COLORS.dangerSoft : COLORS.infoSoft,
                        color: schedule.urgent ? COLORS.dangerText : COLORS.info,
                      }}
                    >
                      {schedule.label}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {error && (
        <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginTop: '12px' }}>{error}</div>
      )}
      {total > items.length && (
        <p style={{ margin: '8px 0 0', fontSize: '12px', color: COLORS.textSub }}>
          이 기간 계약 {total.toLocaleString()}건 중 1,000건만 보입니다.
        </p>
      )}
    </section>
  );
}
